import React, { useState } from 'react';
import './Application.css';

const ORDER_EMAIL = 'ejbusiness2236@gmail.com';

function buildOrderMailto(formData) {
  const subject = `Order: ${formData.firstName.trim()} ${formData.lastName.trim()}`;
  const body = [
    '3D Printing Request / Order',
    '',
    '--- Order details ---',
    `Description: ${formData.description.trim()}`,
    `Quantity: ${formData.quantity.trim()}`,
    '',
    '--- Contact information ---',
    `First Name: ${formData.firstName.trim()}`,
    `Last Name: ${formData.lastName.trim()}`,
    `Phone: ${formData.phone.trim()}`,
    `Email: ${formData.email.trim() || 'Not provided'}`,
    ''
  ].join('\n');

  // Use encodeURIComponent so spaces become %20, not + (URLSearchParams uses + for spaces)
  const query = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return `mailto:${ORDER_EMAIL}?${query}`;
}

function Application() {
  const [formData, setFormData] = useState({
    description: '',
    quantity: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digit characters for validation
    const phoneDigits = phone.replace(/\D/g, '');
    // Valid if it has 10-15 digits (supports international formats)
    return phoneDigits.length >= 10 && phoneDigits.length <= 15;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate description first (required)
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    // Validate quantity
    if (!formData.quantity.trim()) {
      newErrors.quantity = 'Quantity is required';
    } else {
      const quantityNum = parseInt(formData.quantity, 10);
      if (isNaN(quantityNum) || quantityNum < 1) {
        newErrors.quantity = 'Please enter a valid quantity (at least 1)';
      }
    }

    // Validate name
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Validate last name
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Validate phone (required)
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Validate email (optional, but must be valid if provided)
    if (formData.email.trim() && !validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Open default mail app with prepopulated email (works on mobile and desktop)
    setIsSubmitting(true);
    const mailtoUrl = buildOrderMailto(formData);
    window.location.href = mailtoUrl;
    setSubmitted(true);
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <main className="application">
        <div className="application-container">
          <div className="success-message">
            <h2>Almost Done!</h2>
            <p>Your default email app should have opened with your order details.</p>
            <p>Please send the email to complete your request. We will contact you soon regarding your order.</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="application">
      <div className="application-container">
        <h1 className="application-title">3D Printing Request</h1>
        <p className="application-subtitle">Fill out the form below to request a custom 3D print</p>
        
        <form className="application-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`form-input ${errors.description ? 'error' : ''}`}
              rows="6"
              placeholder="Describe what you would like to have 3D printed..."
              required
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="quantity" className="form-label">
              Quantity <span className="required">*</span>
            </label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className={`form-input ${errors.quantity ? 'error' : ''}`}
              placeholder="1"
              min="1"
              required
            />
            {errors.quantity && <span className="error-message">{errors.quantity}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`form-input ${errors.firstName ? 'error' : ''}`}
                placeholder="John"
                required
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="lastName" className="form-label">
                Last Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`form-input ${errors.lastName ? 'error' : ''}`}
                placeholder="Doe"
                required
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`form-input ${errors.phone ? 'error' : ''}`}
                placeholder="(555) 123-4567"
                required
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="john.doe@example.com"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          </div>

          {errors.submit && <span className="error-message">{errors.submit}</span>}

          <div className="form-note">
            <p><span className="required">*</span> Required fields</p>
            <p>Email address is optional</p>
          </div>

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </main>
  );
}

export default Application;

